/**
 * The profile page (docs/UI.md "Profile"): you and your motor in the lock-up yard at night, with the record on
 * a card — the hero shot. Opened from the pause menu ("Your profile"), the book's HOME tile or `profileOpen()`.
 *
 *  - The picture is a small scene drawn INSTEAD of the city (render/sceneOverride.ts, as Pike's studio):
 *    profileScene.ts is the yard; your figure is a fresh character rig wearing the detailed player model, idling
 *    and looking at the camera; your motor is a stand-in vehicle (never in the physics world) drawn through the
 *    ordinary vehicle view factory with your paint and Pike's mods, so every rim, the camber, the neon and the
 *    livery come out as they do on the street. `vehicleSolo` lends it the wheel batches, as the studio does.
 *  - Offline your motor is the save's one car (Lockup 7 / Halloran's): the live one when it is in the world,
 *    else built from the saved build sheet. Online it is your owned car when it is on the street (the server's
 *    mods come with it); a stored car is drawn in its paint alone (the server sends no mods for it: open point).
 *  - The words (profileStats.ts) come from the save offline and from `characterRecord` online.
 *  - The camera is the game camera on a rig override (orbit by drag, a slow sway on its own, pinch to come
 *    closer); the simulation runs on underneath online, offline time waits as it does for the book.
 *  - Everything is built on open and disposed on close: the scene, the figure, the car's view and its wheel
 *    slots, the environment map. `profileState()` reports the renderer's live geometry / texture counts so a
 *    test can check that opening and closing leaves nothing behind.
 */
import type * as THREE from 'three';
import type { GameContext } from '../core/context';
import type { System } from '../core/system';
import type { CameraOverride } from '../camera/cameraRig';
import { touchUi } from '../input/touch';
import { LOCKUP_UNLOCK_ID } from '../economy/wallet';
import { MISSIONS } from '../missions/missions';
import { CONTACT_TRACKS, type ContactTrack } from '../../shared/contacts';
import { sanitizeMods, stockMods, type CarMods } from '../../shared/garage';
import { lookWith, modsFromLook } from '../garage/look';
import { sceneOverride } from '../render/sceneOverride';
import { qualityTier } from '../render/qualityTier';
import { estimateGpuMemory } from '../render/budget';
import { createCharacterRig, PLAYER_VILLAIN, relativeYaw, type AnimInput, type CharacterRig } from '../render/characters';
import type { DetailedFigure } from '../render/characters/detailedPlayer';
import { CAR_LOOK_KEY, type CarLook } from '../vehicles/carTraits';
import { requestRimModels } from '../vehicles/rimModels';
import { VEHICLE_SPECS } from '../vehicles/specs';
import type { Vehicle, VehicleSpec, VehicleTelemetry } from '../vehicles/types';
import { defaultVehicleMeshFactory, vehicleSolo, type VehicleRig } from '../vehicles/vehicleView';
import { firstHour, firstHourHooks } from '../online-ui/firstHour';
import { buildProfileSet, CAR_SPOT, FIGURE_SPOT, FOCUS, type ProfileSet } from './profileScene';
import { ProfileScreen, type ProfileController, type ProfileData } from './profileScreen';
import { bestTitle, carCaption, offlineNotoriety, offlineRows, onlineNotoriety, onlineRows } from './profileStats';

export interface ProfileService {
  readonly isOpen: boolean;
  /** Put the page up (false when the screen is busy: a result sheet, another overlay, the title). */
  open(): boolean;
  close(): void;
  /** The live line for the book's tile ("★★☆☆☆ · A name on the manor"). */
  status(): string;
}

declare module '../core/services' {
  interface ServiceMap {
    profile: ProfileService;
  }
}

declare module '../core/hooks' {
  interface BhHooks {
    profileOpen(): boolean;
    profileClose(): void;
    /** Read-out: open, the motor drawn (spec, live or stand-in), the figure's model, the orbit, the renderer's live GPU object counts. */
    profileState(): Record<string, unknown>;
    /** Turn the picture (degrees) and come closer (factor). */
    profileOrbit(dYawDeg: number, dPitchDeg: number, zoom?: number): void;
    /** The clean picture on / off (Snapshot). */
    profileSnapshot(on: boolean): boolean;
    /**
     * Screenshots / previews: show this motor (spec id, paint, Pike's mods) and these words instead of what the
     * save or the server says, from the next open; null puts it back.
     */
    profilePreview(p: { spec?: string; paint?: number; mods?: Partial<CarMods>; name?: string; kicker?: string; title?: string } | null): boolean;
  }
}

/** Drawn a touch smaller than the street's 1.22× player: at eye level beside a real-sized car he reads right. */
const FIGURE_SCALE = 1.08;
const PITCH_MIN = 3;
const PITCH_MAX = 42;
const ZOOM_MIN = 0.55;
const ZOOM_MAX = 1.7;
/** The shot: distance to the focus point, elevation, the height the camera looks at. */
const SHOT = { distance: 7.6, pitch: 11, lookHeight: 1.0 };
/** Stand-in vehicle ids start here (far above any live id). */
const STUB_ID = 1_000_003;

/** A stand-in for the vehicle view factory: a car that is only ever drawn. */
function stubVehicle(id: number, spec: VehicleSpec, paint: number, look: CarLook | undefined): Vehicle {
  const telemetry: VehicleTelemetry = {
    forwardSpeed: 0,
    lateralSpeed: 0,
    skid: 0,
    wheelSkid: [0, 0, 0, 0],
    frontSlip: 0,
    rearSlip: 0,
    wheelspin: 0,
    braking: false,
    reversing: false,
    steer: 0,
    wheelAngle: 0,
    longAccel: 0,
    latAccel: 0,
    gear: 1,
    rpm: 0,
    load: 0,
    throttle: 0,
    scrape: 0,
    impact: 0,
    topSpeed: spec.maxSpeed,
  };
  const data: Record<string, unknown> = {};
  if (look) data[CAR_LOOK_KEY] = { ...look };
  const v = {
    id,
    spec,
    controls: { throttle: 0, steer: 0, handbrake: true },
    controller: 'none',
    occupant: null,
    isPolice: false,
    ownedByPlayer: true,
    stolen: false,
    locked: true,
    health: spec.maxHealth,
    damage: { front: 0, rear: 0, left: 0, right: 0 },
    punctures: { left: 0, right: 0 },
    destroyed: false,
    condition: 'ok',
    fireTimer: 0,
    lastPlayerHitTime: -1e9,
    damageVersion: 0,
    dents: [],
    siren: false,
    // Lit sidelights at night: the lamps glow in the paint (the road pools stay off on show).
    headlights: true,
    paint,
    telemetry,
    tags: new Set<string>(),
    data,
    pos: { x: 0, z: 0 },
    heading: 0,
    velocity: { x: 0, z: 0 },
    speed: 0,
  };
  return v as unknown as Vehicle;
}

export function createProfileSystem(): System {
  let ctx: GameContext;
  let screen: ProfileScreen | null = null;
  let set: ProfileSet | null = null;
  /** What buildProfileSet took on the last open (ms), for profileState. */
  let buildMs = 0;
  let active = false;
  let hudHidden = false;
  let frozeTime = false;
  // the figure
  let figRig: CharacterRig | null = null;
  let figure: DetailedFigure | null = null;
  let figureState: 'none' | 'loading' | 'detailed' | 'failed' = 'none';
  const figInput: AnimInput = { dt: 0, speed: 0, running: false, turnRate: 0, action: 'none', actionProgress: 0, heading: 0 };
  // the motor
  let car: Vehicle | null = null;
  let carRig: VehicleRig | null = null;
  let carLive = false;
  let carMods: CarMods = stockMods();
  let carKept: string | null = null;
  let carModsUnknown = false;
  let time = 0;
  // the camera
  const orbit = { yaw: 0, pitch: 0, zoom: 1 };
  const override: CameraOverride = { target: { x: 0, z: 0 }, distance: SHOT.distance, pitchDeg: SHOT.pitch, yawOffsetDeg: 0, blend: 6, lookHeight: SHOT.lookHeight, orbitRate: 9 };
  const spot = { x: 0, z: 0 };
  let recordVersion = -1;
  /** `profilePreview`: a motor and words for screenshots, instead of the real ones. */
  let preview: { spec?: string; paint?: number; mods?: Partial<CarMods>; name?: string; kicker?: string; title?: string } | null = null;

  const ui = () => ctx.services.tryGet('ui');
  const online = (): boolean => !!ctx.services.tryGet('net')?.online;

  // ------------------------------------------------------------------ the words

  function rungs(): Partial<Record<ContactTrack, number>> {
    const c = ctx.services.tryGet('contacts');
    const out: Partial<Record<ContactTrack, number>> = {};
    if (c) for (const t of CONTACT_TRACKS) out[t] = c.rung(t);
    return out;
  }

  function data(): ProfileData {
    const r = rungs();
    const motor = car && carRig ? carCaption({ name: car.spec.displayName, paint: car.paint, mods: carMods, kept: carKept, modsUnknown: carModsUnknown }) : null;
    const d = words(r, motor);
    if (preview?.name) d.name = preview.name;
    if (preview?.kicker) d.kicker = preview.kicker;
    if (preview?.title) d.title = preview.title;
    return d;
  }

  function words(r: Partial<Record<ContactTrack, number>>, motor: ReturnType<typeof carCaption> | null): ProfileData {
    if (online()) {
      const net = ctx.services.tryGet('net');
      const game = ctx.services.tryGet('online');
      const rec = firstHour.record;
      const g = game?.store.state.gang ?? null;
      const gang = g ? { name: g.name, tag: g.tag, role: g.roles.find((x) => x.id === g.you.role)?.name ?? 'member', tier: g.tier.tier } : null;
      const econ = game?.store.state.econ;
      const input = { cash: (econ?.cash ?? 0) + (econ?.bank ?? 0), rec, gang, rungs: r };
      const n = onlineNotoriety(input);
      const title = bestTitle(r);
      return {
        kicker: `${net?.shard?.name ?? 'The city'} · online`,
        name: net?.session?.character.name ?? 'You',
        title: title ? `“${title.title}” — ${title.contact}` : n.name,
        notoriety: n,
        rows: onlineRows(input),
        car: motor,
        carNote: motor ? null : 'No motor of your own yet — buy one at a garage door, or keep one at Pike’s.',
      };
    }
    const save = ctx.services.get('save').data;
    const input = {
      money: ctx.services.tryGet('economy')?.balance ?? save.money,
      stats: save.stats,
      completedMissions: save.completedMissions,
      story: MISSIONS.map((m) => m.id),
      activities: save.activities,
      weapons: save.arsenal.owned.filter((w) => w !== 'fists').length,
      rungs: r,
    };
    const n = offlineNotoriety(input);
    const title = bestTitle(r);
    return {
      kicker: 'Low Borough · October 1970',
      name: n.name,
      title: title ? `“${title.title}” — ${title.contact}` : 'Nobody’s heard of you. Yet.',
      notoriety: n,
      rows: offlineRows(input),
      car: motor,
      carNote: motor ? null : 'No motor of your own yet — see Des at Halloran’s, or keep one at Pike’s.',
    };
  }

  // ------------------------------------------------------------------ the motor

  /** Your car: the live one when it is in the world, else a stand-in from what is kept. Null: none. */
  function findCar(): { v: Vehicle; live: boolean; mods: CarMods; kept: string | null; modsUnknown: boolean } | null {
    const vehicles = ctx.services.tryGet('vehicles');
    if (preview?.spec) {
      const spec = VEHICLE_SPECS[preview.spec];
      if (!spec) return null;
      const mods = sanitizeMods({ ...stockMods(), ...preview.mods });
      const stub = stubVehicle(STUB_ID + 7, spec, preview.paint ?? spec.paints[0] ?? 0x3a3f4a, lookWith(undefined, mods));
      return { v: stub, live: false, mods, kept: 'Lockup 7, Pilcher Ginnel', modsUnknown: false };
    }
    if (online()) {
      const net = ctx.services.tryGet('net');
      const game = ctx.services.tryGet('online');
      const cid = net?.session?.character.id ?? -1;
      if (net && vehicles) {
        for (const v of vehicles.all()) {
          const info = net.vehicleInfo(v);
          if (!info || info.cat !== 'owned' || info.ownerCid !== cid || v.destroyed) continue;
          return { v, live: true, mods: info.mods, kept: 'Out on the street', modsUnknown: false };
        }
      }
      const owned = (game?.store.state.econ?.vehicles ?? []).filter((o) => !o.loaner && o.status !== 'destroyed');
      const o = owned.find((x) => x.status === 'stored') ?? owned[0];
      const spec = o ? VEHICLE_SPECS[o.spec] : undefined;
      if (!o || !spec) return null;
      const kept = o.status === 'stored' ? 'Stored at Halloran Motors' : o.status === 'impounded' ? 'Impounded — the law has it' : 'Out where you left it';
      return { v: stubVehicle(STUB_ID + o.id, spec, o.paint, undefined), live: false, mods: stockMods(), kept, modsUnknown: true };
    }
    const garage = ctx.services.tryGet('garage');
    const save = ctx.services.tryGet('save');
    const kept = save?.hasUnlock(LOCKUP_UNLOCK_ID) ? 'Lockup 7, Pilcher Ginnel' : 'Halloran’s forecourt';
    if (garage && vehicles) {
      for (const v of vehicles.all()) {
        if (!v.destroyed && garage.isMine(v)) return { v, live: true, mods: modsFromLook(v.data[CAR_LOOK_KEY] as CarLook | undefined), kept, modsUnknown: false };
      }
    }
    const motor = save?.data.activities.motor ?? null;
    const spec = motor ? VEHICLE_SPECS[motor.spec] : undefined;
    if (!motor || !spec || !garage) return null;
    const stub = stubVehicle(STUB_ID, spec, motor.build.paint ?? spec.paints[0] ?? 0x3a3f4a, undefined);
    garage.preview(stub, motor.build);
    return { v: stub, live: false, mods: modsFromLook(stub.data[CAR_LOOK_KEY] as CarLook | undefined), kept, modsUnknown: false };
  }

  // ------------------------------------------------------------------ open / close

  function open(): boolean {
    const u = ui();
    if (active || !ctx.render || !ctx.uiRoot || !u) return false;
    const root = ctx.uiRoot.querySelector('.bh-ui') as HTMLElement | null;
    if (!root) return false;
    const s = new ProfileScreen(root, controller);
    if (!u.openOverlay(s, 'profile', () => teardown())) {
      s.dispose();
      return false;
    }
    screen = s;
    active = true;
    ctx.uiRoot.classList.add('pf-open');
    u.setHudVisible(false);
    hudHidden = true;
    ctx.services.get('player').controls.acquire('profile');
    ctx.input.clearTouch?.();
    // Offline the world waits while you look (as for the book); online the city is shared.
    if (!online() && ctx.loop.timeScale > 0) {
      frozeTime = true;
      ctx.loop.timeScale = 0;
    }

    // The yard, stood round the player (the camera orbits near where it already is).
    const tier = ctx.services.tryGet('renderPipeline')?.quality ?? 'low';
    const t0 = performance.now();
    set = buildProfileSet(ctx.render.renderer, tier);
    buildMs = performance.now() - t0;
    const rig = ctx.services.tryGet('cameraRig');
    const p = ctx.services.get('player').pos;
    set.place(p.x, p.z, rig?.yaw ?? Math.PI);

    // Your motor, through the ordinary vehicle view, lent the wheel batches.
    const found = findCar();
    if (found) {
      car = found.v;
      carLive = found.live;
      carMods = found.mods;
      carKept = found.kept;
      carModsUnknown = found.modsUnknown;
      vehicleSolo.id = car.id;
      vehicleSolo.scene = set.scene;
      requestRimModels();
      const factory = ctx.services.tryGet('vehicleMeshFactory') ?? defaultVehicleMeshFactory;
      carRig = factory(car);
      set.scene.add(carRig.root);
    }

    // You: a fresh rig wearing the detailed model (the rig's own body stands in until it lands).
    figRig = createCharacterRig(PLAYER_VILLAIN, { scale: FIGURE_SCALE, seed: 0x1970 });
    figRig.root.name = 'profile-figure';
    set.scene.add(figRig.root);
    figureState = 'loading';
    void import('../render/characters/detailedPlayer')
      .then(async (m) => [m, await m.loadDetailedModel('player', qualityTier() === 'low' ? 'low' : 'high')] as const)
      .then(([m, gltf]) => {
        if (!active || !figRig || figure) return;
        figure = m.createDetailedFigure(gltf, figRig.skeleton);
        figRig.root.add(figure.root);
        figRig.setBodyVisible(false);
        figureState = 'detailed';
      })
      .catch((e: unknown) => {
        figureState = 'failed';
        console.warn('[profile] detailed figure failed to load; drawing the rig body instead', e);
      });

    sceneOverride.scene = set.scene;
    sceneOverride.camera = ctx.camera;
    orbit.yaw = 0;
    orbit.pitch = 0;
    orbit.zoom = 1;
    time = 0;
    place();
    syncCamera(true);
    if (online()) firstHourHooks.askRecord();
    recordVersion = firstHour.version;
    s.render(data());
    const audio = ctx.services.tryGet('audio');
    audio?.ui('whoosh');
    // The yard's own soundscape: drizzle on the roof, far traffic, the radio inside the lock-up.
    audio?.yard(true);
    return true;
  }

  function teardown(): void {
    if (!active) return;
    active = false;
    const s = screen;
    screen = null;
    if (s) {
      ui()?.closeOverlay(s);
      s.dispose();
    }
    ctx.uiRoot?.classList.remove('pf-open');
    if (hudHidden) {
      hudHidden = false;
      ui()?.setHudVisible(true);
    }
    ctx.services.get('player').controls.release('profile');
    ctx.services.tryGet('audio')?.yard(false);
    ctx.services.tryGet('cameraRig')?.setOverride(null);
    if (frozeTime) {
      frozeTime = false;
      if (ctx.loop.timeScale === 0) ctx.loop.timeScale = 1;
    }
    if (set && sceneOverride.scene === set.scene) {
      sceneOverride.scene = null;
      sceneOverride.camera = null;
    }
    // The car's view and its wheel slots; a live car's own view never left the city.
    vehicleSolo.id = null;
    vehicleSolo.scene = null;
    if (carRig) {
      carRig.root.removeFromParent();
      carRig.dispose();
    }
    carRig = null;
    car = null;
    figure?.dispose();
    figure = null;
    figRig?.dispose();
    figRig = null;
    figureState = 'none';
    set?.dispose();
    set = null;
  }

  function close(): void {
    if (!active) return;
    teardown();
    ctx.services.tryGet('audio')?.ui('back');
  }

  // ------------------------------------------------------------------ the picture

  /** The set follows the player and the camera's base yaw; the motor and you stand on your spots in it. */
  function place(): void {
    if (!set) return;
    const p = ctx.services.get('player').pos;
    const yaw = ctx.services.tryGet('cameraRig')?.yaw ?? Math.PI;
    set.place(p.x, p.z, yaw);
    if (carRig) {
      set.toWorld(CAR_SPOT.x, CAR_SPOT.z, spot);
      carRig.root.position.set(spot.x, 0, spot.z);
      carRig.root.rotation.y = set.heading(CAR_SPOT.heading);
    }
    if (figRig) {
      set.toWorld(FIGURE_SPOT.x, FIGURE_SPOT.z, spot);
      figRig.root.position.set(spot.x, 0, spot.z);
      figRig.root.rotation.y = set.heading(FIGURE_SPOT.heading);
    }
  }

  function syncCamera(snap = false): void {
    const rig = ctx.services.tryGet('cameraRig');
    if (!rig || !set) return;
    // A slow sway on its own; what you dragged eases back over a few seconds.
    const sway = Math.sin(time * 0.23) * 5;
    const tilt = Math.sin(time * 0.17 + 1) * 1.2;
    set.toWorld(FOCUS.x, FOCUS.z, spot);
    override.target.x = spot.x;
    override.target.z = spot.z;
    override.distance = SHOT.distance * orbit.zoom;
    override.pitchDeg = Math.max(PITCH_MIN, Math.min(PITCH_MAX, SHOT.pitch + tilt + orbit.pitch));
    override.yawOffsetDeg = sway + orbit.yaw;
    override.lookHeight = SHOT.lookHeight;
    override.blend = snap ? 6 : 4;
    override.orbitRate = 9;
    rig.setOverride(override);
    if (snap) rig.snap();
  }

  /** The frame as it stands, to the share sheet (a phone) or a download. Called on the way into the clean picture. */
  function capture(): void {
    const r = ctx.render;
    const pipeline = ctx.services.tryGet('renderPipeline');
    if (!r || !pipeline || typeof document === 'undefined') return;
    try {
      // Draw once more now (the panels are already off) and read the frame back in the same task.
      pipeline.render(0);
      r.canvas.toBlob((blob) => {
        if (!blob) return;
        const file = new File([blob], 'borough-heat-profile.png', { type: 'image/png' });
        const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean };
        if (nav.canShare?.({ files: [file] }) && typeof nav.share === 'function') {
          nav.share({ files: [file], title: 'Borough Heat' }).catch(() => undefined);
          return;
        }
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = file.name;
        a.click();
        setTimeout(() => URL.revokeObjectURL(a.href), 5000);
      }, 'image/png');
    } catch (e) {
      console.warn('[profile] snapshot failed', e);
    }
  }

  const controller: ProfileController = {
    touch: () => touchUi.enabled,
    close,
    orbit(dYaw, dPitch) {
      orbit.yaw += dYaw;
      orbit.pitch = Math.max(PITCH_MIN - SHOT.pitch, Math.min(PITCH_MAX - SHOT.pitch, orbit.pitch + dPitch));
    },
    zoom(f) {
      orbit.zoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, orbit.zoom * f));
    },
    snapshot(on) {
      // The panels fade over 0.25 s; capture once they are gone.
      if (on) setTimeout(() => active && screen?.isClean && capture(), 320);
    },
    sound: (s) => ctx.services.tryGet('audio')?.ui(s),
  };

  const service: ProfileService = {
    get isOpen() {
      return active;
    },
    open,
    close,
    status() {
      const d = data();
      return `${'★'.repeat(d.notoriety.stars)}${'☆'.repeat(5 - d.notoriety.stars)} · ${d.notoriety.name}`;
    },
  };

  return {
    id: 'profile',
    order: 717,
    init(c) {
      ctx = c;
      c.services.provide('profile', service);
      c.hooks.register('profileOpen', () => open());
      c.hooks.register('profileClose', () => close());
      c.hooks.register('profileOrbit', (dy, dp, z = 1) => {
        controller.orbit(dy, dp);
        controller.zoom(z);
      });
      c.hooks.register('profileSnapshot', (on) => {
        if (!screen) return false;
        screen.setClean(on);
        return true;
      });
      c.hooks.register('profilePreview', (p) => {
        preview = p && typeof p === 'object' ? { ...p } : null;
        return !!preview;
      });
      c.hooks.register('profileState', () => {
        const info = c.render?.renderer.info;
        return {
          open: active,
          car: car ? { spec: car.spec.id, id: car.id, live: carLive, paint: car.paint, mods: { ...carMods }, kept: carKept } : null,
          figure: figureState,
          clean: screen?.isClean ?? false,
          orbit: { ...orbit },
          // The yard's own cost (textures, geometry and the shadow map of this scene alone, MB) while it is up.
          scene: set && c.render ? { objects: countObjects(set.scene), gpuMB: estimateGpuMemory(c.render.renderer, set.scene, 0, false) } : null,
          // What building the yard cost on open (ms): its canvases, geometry and the PMREM.
          buildMs: set ? Math.round(buildMs * 10) / 10 : null,
          memory: info ? { geometries: info.memory.geometries, textures: info.memory.textures, programs: info.programs?.length ?? 0 } : null,
          words: active ? data() : null,
        };
      });
    },
    update(dt) {
      if (!active) return;
      time += dt;
      // The page ends with the game underneath: an arrest, a death, the UI forcing it shut (openOverlay's callback).
      if (ctx.services.tryGet('economy')?.outcomePending) {
        teardown();
        return;
      }
      // What you dragged eases back toward the shot.
      const k = 1 - Math.exp(-0.45 * dt);
      orbit.yaw -= orbit.yaw * k;
      orbit.pitch -= orbit.pitch * k;
      place();
      syncCamera();
      set?.update(dt);
      if (carRig && car) carRig.sync(car, dt, time);
      if (figRig) {
        // Idle, with his eyes on the camera (the animator keeps the look within reason).
        const cam = ctx.camera.position;
        const fp = figRig.root.position;
        figInput.dt = dt;
        figInput.heading = figRig.root.rotation.y;
        figInput.lookYaw = relativeYaw(fp.x, fp.z, figRig.root.rotation.y, cam.x, cam.z);
        figInput.lookWeight = 0.75;
        figRig.update(figInput);
        figure?.sync(figRig, dt, null);
      }
      // The server's record landing (online): the card redraws.
      if (firstHour.version !== recordVersion) {
        recordVersion = firstHour.version;
        screen?.render(data());
      }
    },
    dispose() {
      if (active) teardown();
    },
  };
}

function countObjects(scene: THREE.Object3D): number {
  let n = 0;
  scene.traverse(() => n++);
  return n;
}
